import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { BsReceipt, BsSearch } from "react-icons/bs";
import { useNavigate } from "react-router-dom";
import { PROTECTED_PATHS } from "../../app/constants";
import Container from "../../components/Container";
import Footer from "../../components/footer";
import FullPageLoader from "../../components/FullPageLoader";
import Header from "../../components/Header";
import { COLORS } from "../../constants/colors";
import { fetchFoodListings, fetchVendors } from "./service";
import FoodCard from "./components/FoodCard";

const FoodListings = () => {
  const pageBg = useColorModeValue(COLORS.bgGrey, COLORS.darkModeBg);
  const cardBg = useColorModeValue(COLORS.white, COLORS.grey);
  const heroBg = useColorModeValue(COLORS.primary, COLORS.black);
  const appTextColor = useColorModeValue(COLORS.primary, COLORS.lightGrey);
  const mutedColor = useColorModeValue("#707070", COLORS.lightGrey);
  const searchBg = useColorModeValue("#EFEFEF", "#1f1f2e");
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [foods, setFoods] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    fetchFoodListings(setLoading, setFoods, setError);
    fetchVendors(setVendors);
  }, []);

  const filteredFoods = useMemo(() => {
    const query = search.trim().toLowerCase();

    return foods.filter((item) => {
      const matchesVendor =
        !selectedVendor ||
        item?.vendorId === selectedVendor ||
        item?.vendorName?.toLowerCase() === selectedVendor.toLowerCase();

      const matchesSearch =
        !query ||
        [item?.name, item?.category, item?.location, item?.vendorName]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      return matchesVendor && matchesSearch;
    });
  }, [foods, search, selectedVendor]);

  const vendorOptions = useMemo(() => {
    const apiVendors = vendors.map((vendor) => ({
      id: vendor.id,
      name: vendor.name,
    }));

    const foodVendors = foods
      .map((item) => ({
        id: item.vendorId || item.vendorName,
        name: item.vendorName,
      }))
      .filter((vendor) => vendor.id && vendor.name);

    return [...apiVendors, ...foodVendors].filter(
      (vendor, index, list) =>
        index ===
        list.findIndex(
          (item) =>
            item.id === vendor.id ||
            item.name.toLowerCase() === vendor.name.toLowerCase()
        )
    );
  }, [foods, vendors]);

  if (loading) return <FullPageLoader bg={pageBg} />;

  return (
    <Stack pb="15vh" bg={pageBg} minH="100vh">
      <Box shadow="sm">
        <Container>
          <Header menu />
        </Container>
      </Box>

      <Container>
        <Stack spacing="5" pt="5" pb="10">
          <Box bg={heroBg} borderRadius="16px" p="5" color={COLORS.white}>
            <Flex justify="space-between" align="center" gap="3">
              <Text fontSize="xl" fontWeight="700">
                All Food Listings
              </Text>
              <Button
                size="sm"
                leftIcon={<BsReceipt />}
                bg="rgba(255,255,255,0.14)"
                color={COLORS.white}
                _hover={{ bg: "rgba(255,255,255,0.22)" }}
                onClick={() => navigate(PROTECTED_PATHS.FOOD_ORDERS)}
                flexShrink="0"
              >
                Orders
              </Button>
            </Flex>
            <Text mt="1" fontSize="sm" opacity="0.9">
              Browse meals currently listed by food vendors across estates.
            </Text>
            <Text mt="3" fontSize="xs" opacity="0.8">
              {foods.length} listing{foods.length === 1 ? "" : "s"} found
            </Text>
          </Box>

          <InputGroup>
            <InputLeftElement pointerEvents="none" children={<BsSearch color={mutedColor} />} />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by food, location, category, or vendor"
              border="none"
              borderRadius="999px"
              bg={searchBg}
              color={appTextColor}
              _placeholder={{ color: mutedColor }}
            />
          </InputGroup>

          <Select
            value={selectedVendor}
            onChange={(event) => setSelectedVendor(event.target.value)}
            border="none"
            borderRadius="999px"
            bg={searchBg}
            color={appTextColor}
          >
            <option value="">All vendors</option>
            {vendorOptions.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </Select>

          {error ? (
            <Flex bg={cardBg} borderRadius="12px" p="4" justify="center">
              <Text textAlign="center" color={appTextColor}>
                {error}
              </Text>
            </Flex>
          ) : null}

          {!error && filteredFoods.length === 0 ? (
            <Flex bg={cardBg} borderRadius="12px" p="6" justify="center">
              <Text textAlign="center" color={appTextColor}>
                No food listings match your search.
              </Text>
            </Flex>
          ) : null}

          <SimpleGrid columns={[1, 1, 2]} spacing="4">
            {filteredFoods.map((item) => (
              <FoodCard key={item.id} item={item} />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>

      <Footer />
    </Stack>
  );
};

export default FoodListings;
